import { useState, useEffect } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import { GLOBALES_DEFAULT, calcImportacion, fmtUSD, fmtARS, getNcmPorRubro } from "../../utils/ddp";

const G = {
  bg:"#FAF9F7",s1:"#FFFFFF",s2:"#F4F2EF",border:"#E2DFD9",border2:"#D4D0CA",
  black:"#1C1917",off:"#3D3830",muted:"#8C8078",dim:"#C8C0B8",
  orange:"#D2611A",green:"#16A34A",red:"#DC2626",amber:"#D97706",blue:"#2563EB",
  sans:"'Outfit','Helvetica Neue',Arial,sans-serif",
  mono:"'IBM Plex Mono',monospace",
};

function Campo({ label, value, onChange, note="", type="number" }) {
  return (
    <div style={{marginBottom:16}}>
      <div style={{fontFamily:G.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",color:G.muted,marginBottom:5,fontWeight:600}}>{label}</div>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)}
        style={{width:"100%",background:"transparent",border:"none",borderBottom:`1.5px solid ${G.border2}`,color:G.black,fontFamily:G.mono,fontSize:16,padding:"6px 0",outline:"none"}}
        onFocus={e=>e.target.style.borderBottomColor=G.orange}
        onBlur={e=>e.target.style.borderBottomColor=G.border2}
      />
      {note&&<div style={{fontFamily:G.mono,fontSize:9,color:G.muted,marginTop:3}}>{note}</div>}
    </div>
  );
}

function Seccion({ titulo, children }) {
  return (
    <div style={{marginBottom:28}}>
      <div style={{fontFamily:G.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",color:G.muted,marginBottom:14,fontWeight:600,paddingBottom:8,borderBottom:`1px solid ${G.border}`}}>{titulo}</div>
      {children}
    </div>
  );
}

export default function AdminGlobales({ onUpdate }) {
  const [form,    setForm]    = useState({ ...GLOBALES_DEFAULT });
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  // Calculadora de prueba
  const [testFob, setTestFob] = useState("100");
  const [testCbm, setTestCbm] = useState("0.2");
  const [testRub, setTestRub] = useState("Pisos SPC");

  useEffect(()=>{
    getDoc(doc(db,"configuracion","globales")).then(snap=>{
      if (snap.exists()) setForm(f=>({...f,...snap.data()}));
    });
  },[]);

  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const setN = (k,v) => { const n=parseFloat(v); setForm(p=>({...p,[k]:isNaN(n)?p[k]:n})); };

  const guardar = async () => {
    setSaving(true);
    const data = Object.fromEntries(Object.entries(form).map(([k,v])=>[k,typeof v==="string"?parseFloat(v)||v:v]));
    await setDoc(doc(db,"configuracion","globales"), { ...data, updatedAt:serverTimestamp() });
    setSaving(false); setSaved(true);
    setTimeout(()=>setSaved(false), 2500);
    if (onUpdate) onUpdate(data);
  };

  // Calculadora de prueba en tiempo real
  const testResult = (parseFloat(testFob)>0 && parseFloat(testCbm)>0)
    ? calcImportacion({ fob_compra:testFob, fob_declarado:testFob, cbm:testCbm, globales:form, rubro:testRub })
    : null;

  const RUBROS = ["Sanitarios","Bañeras","Vanitorys","Pisos SPC","Hornos y Anafes","Puertas","Carpintería","Cocinas"];

  return (
    <div style={{display:"grid",gridTemplateColumns:"280px 1fr",gap:32,background:G.bg}}>

      {/* ── Panel izquierdo: inputs ── */}
      <div>
        <div style={{fontFamily:G.sans,fontSize:18,color:G.black,fontWeight:700,marginBottom:4}}>Variables globales</div>
        <div style={{fontFamily:G.sans,fontSize:11,color:G.muted,marginBottom:24,lineHeight:1.5}}>Afectan el cálculo de importación de todos los productos</div>

        <Seccion titulo="Tipo de cambio">
          <Campo label="ARS por USD (Dólar BNA)" value={form.tipo_cambio} onChange={v=>setN("tipo_cambio",v)} note="Actualizar diariamente"/>
          <Campo label="Dólar Blue (referencia)" value={form.tipo_cambio_blue} onChange={v=>setN("tipo_cambio_blue",v)}/>
        </Seccion>

        <Seccion titulo="Contenedor">
          <div style={{marginBottom:16}}>
            <div style={{fontFamily:G.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",color:G.muted,marginBottom:5,fontWeight:600}}>Tipo de contenedor</div>
            <select value={form.tipo_contenedor} onChange={e=>set("tipo_contenedor",e.target.value)}
              style={{width:"100%",background:"#FFFFFF",color:"#1C1917",WebkitAppearance:"menulist",border:`1px solid ${G.border}`,fontFamily:G.sans,fontSize:13,padding:"7px 10px",outline:"none"}}>
              {[["40HQ",68],["40HC",68],["20GP",34]].map(([t,c])=><option key={t} value={t}>{t} — {c} CBM</option>)}
            </select>
          </div>
          <Campo label="Capacidad CBM" value={form.cbm_max} onChange={v=>setN("cbm_max",v)} note="68 para 40HQ, 34 para 20GP"/>
        </Seccion>

        <Seccion titulo="Logística (USD)">
          <Campo label="Flete declarado total (USD)" value={form.flete_declarado} onChange={v=>setN("flete_declarado",v)} note="Usado para base imponible de aduana"/>
          <Campo label="Flete real total (USD)" value={form.flete_real} onChange={v=>setN("flete_real",v)} note="Lo que realmente pagamos"/>
          <Campo label="Terminal total (USD)" value={form.terminal} onChange={v=>setN("terminal",v)}/>
          <Campo label="Despachante total (USD)" value={form.despachante} onChange={v=>setN("despachante",v)}/>
        </Seccion>

        <Seccion titulo="Logística (ARS)">
          <Campo label="Descarga total (ARS)" value={form.descarga_ars} onChange={v=>setN("descarga_ars",v)} note="Se convierte a USD con TC BNA"/>
          <Campo label="Transporte terminal→depósito (ARS)" value={form.transporte_ars} onChange={v=>setN("transporte_ars",v)} note="Se convierte a USD con TC BNA"/>
        </Seccion>

        <Seccion titulo="Impuestos y costos (%)">
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            <Campo label="Maury %" value={(form.maury_pct*100).toFixed(1)} onChange={v=>setN("maury_pct",parseFloat(v)/100)} note="5%"/>
            <Campo label="Estadística %" value={(form.estadistica_pct*100).toFixed(1)} onChange={v=>setN("estadistica_pct",parseFloat(v)/100)} note="3%"/>
            <Campo label="Seguro %" value={(form.seguro_pct*100).toFixed(1)} onChange={v=>setN("seguro_pct",parseFloat(v)/100)} note="1%"/>
            <Campo label="Certificaciones %" value={((form.certificaciones_pct||0)*100).toFixed(1)} onChange={v=>setN("certificaciones_pct",parseFloat(v)/100)} note="0% por defecto"/>
            <Campo label="IVA %" value={(form.iva_pct*100).toFixed(1)} onChange={v=>setN("iva_pct",parseFloat(v)/100)} note="Recuperable RI"/>
            <Campo label="IVA 2 %" value={(form.iva2_pct*100).toFixed(1)} onChange={v=>setN("iva2_pct",parseFloat(v)/100)} note="No recuperable"/>
            <Campo label="IIBB %" value={(form.iibb_pct*100).toFixed(1)} onChange={v=>setN("iibb_pct",parseFloat(v)/100)} note="4%"/>
          </div>
        </Seccion>

        <button onClick={guardar} disabled={saving}
          style={{width:"100%",padding:"12px 0",background:saved?G.green:G.black,color:"#fff",fontFamily:G.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",border:"none",cursor:"pointer",fontWeight:700,transition:"background .3s"}}>
          {saving?"Guardando...":saved?"✓ Guardado":"Guardar variables"}
        </button>
      </div>

      {/* ── Panel derecho: calculadora de prueba ── */}
      <div>
        <div style={{fontFamily:G.sans,fontSize:16,color:G.black,fontWeight:700,marginBottom:4}}>Calculadora de prueba</div>
        <div style={{fontFamily:G.sans,fontSize:11,color:G.muted,marginBottom:20}}>Verificá el cálculo con las variables actuales</div>

        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
          <Campo label="FOB Venta (USD)" value={testFob} onChange={setTestFob} note="Precio de compra en China"/>
          <Campo label="CBM por unidad" value={testCbm} onChange={setTestCbm} note="Volumen unitario en m³"/>
        </div>
        <div style={{marginBottom:20}}>
          <div style={{fontFamily:G.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",color:G.muted,marginBottom:5,fontWeight:600}}>Rubro</div>
          <select value={testRub} onChange={e=>setTestRub(e.target.value)}
            style={{width:"100%",background:"#FFFFFF",color:"#1C1917",WebkitAppearance:"menulist",border:`1px solid ${G.border}`,fontFamily:G.sans,fontSize:13,padding:"7px 10px",outline:"none"}}>
            {RUBROS.map(r=><option key={r}>{r}</option>)}
          </select>
          <div style={{fontFamily:G.mono,fontSize:9,color:G.orange,marginTop:4}}>
            NCM: {getNcmPorRubro(testRub).ncm} · Arancel: {(getNcmPorRubro(testRub).arancel*100).toFixed(1)}%
          </div>
        </div>

        {testResult ? (
          <>
            {/* Desglose */}
            <div style={{marginBottom:16}}>
              <div style={{fontFamily:G.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",color:G.muted,marginBottom:10,fontWeight:600}}>Desglose por unidad</div>
              {testResult.desglose.map(d=>(
                <div key={d.label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:`1px solid ${G.border}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontFamily:G.sans,fontSize:11,color:G.muted}}>{d.label}</span>
                    {d.tag&&<span style={{fontFamily:G.mono,fontSize:8,color:G.amber,border:`0.5px solid ${G.amber}`,padding:"1px 5px"}}>{d.tag}</span>}
                  </div>
                  <span style={{fontFamily:G.mono,fontSize:11,color:G.off}}>{fmtUSD(d.valor)}</span>
                </div>
              ))}
            </div>

            {/* Totales */}
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
              <div style={{background:G.s2,padding:"14px 16px",border:`1px solid ${G.border}`}}>
                <div style={{fontFamily:G.sans,fontSize:8,color:G.muted,letterSpacing:1.5,textTransform:"uppercase",marginBottom:4,fontWeight:600}}>Total DDP / unidad</div>
                <div style={{fontFamily:G.mono,fontSize:20,color:G.black,fontWeight:700}}>{fmtUSD(testResult.costo_total_unit)}</div>
                <div style={{fontFamily:G.mono,fontSize:10,color:G.muted}}>En pesos (TC {form.tipo_cambio})</div>
                <div style={{fontFamily:G.mono,fontSize:11,color:G.off}}>{fmtARS(testResult.costo_total_ars)}</div>
              </div>
              <div style={{background:"#F0FDF4",padding:"14px 16px",border:`1px solid ${G.green}44`}}>
                <div style={{fontFamily:G.sans,fontSize:8,color:G.muted,letterSpacing:1.5,textTransform:"uppercase",marginBottom:4,fontWeight:600}}>Costo neto (base margen)</div>
                <div style={{fontFamily:G.mono,fontSize:20,color:G.green,fontWeight:700}}>{fmtUSD(testResult.costo_neto_unit)}</div>
                <div style={{fontFamily:G.mono,fontSize:10,color:G.muted}}>Sin IVA recuperable (21%)</div>
                <div style={{fontFamily:G.mono,fontSize:11,color:G.off}}>{fmtARS(testResult.costo_neto_ars)}</div>
              </div>
            </div>

            {/* Uso del contenedor */}
            <div style={{background:G.s2,padding:"12px 14px",border:`1px solid ${G.border}`}}>
              <div style={{fontFamily:G.sans,fontSize:9,letterSpacing:1.5,textTransform:"uppercase",color:G.muted,marginBottom:6,fontWeight:600}}>Uso del contenedor</div>
              <div style={{height:4,background:G.border,borderRadius:2,marginBottom:6}}>
                <div style={{height:4,background:G.orange,width:`${Math.min(testResult.pct_contenedor*100,100)}%`,borderRadius:2}}/>
              </div>
              <div style={{fontFamily:G.mono,fontSize:11,color:G.off}}>
                {(testResult.pct_contenedor*100).toFixed(3)}% — {testCbm} CBM de {form.cbm_max} disponibles
              </div>
            </div>

            {/* Referencia actual */}
            <div style={{marginTop:16,padding:"14px 16px",background:G.s2,border:`1px solid ${G.border}`}}>
              <div style={{fontFamily:G.sans,fontSize:9,letterSpacing:2,textTransform:"uppercase",color:G.muted,marginBottom:12,fontWeight:600}}>Referencia actual</div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12}}>
                {[
                  ["Flete real",`USD ${form.flete_real?.toLocaleString()}`],
                  ["CBM max",`${form.cbm_max} m³`],
                  ["Seguro",`${(form.seguro_pct*100).toFixed(1)}%`],
                  ["Aranceles",`${(getNcmPorRubro(testRub).arancel*100).toFixed(1)}%`],
                  ["IVA",`${(form.iva_pct*100).toFixed(1)}%`],
                  ["IVA Adic.",`${(form.iva2_pct*100).toFixed(1)}%`],
                  ["IIBB",`${(form.iibb_pct*100).toFixed(1)}%`],
                  ["Gs. Nac.",`USD ${(parseFloat(form.despachante)||0)+(parseFloat(form.terminal)||0)}`],
                  ["TC",`${form.tipo_cambio} ARS`],
                ].map(([l,v])=>(
                  <div key={l}>
                    <div style={{fontFamily:G.sans,fontSize:8,color:G.muted,letterSpacing:1.5,textTransform:"uppercase",marginBottom:3,fontWeight:600}}>{l}</div>
                    <div style={{fontFamily:G.mono,fontSize:11,color:G.off}}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div style={{padding:40,textAlign:"center",fontFamily:G.sans,fontSize:12,color:G.muted,background:G.s2,border:`1px solid ${G.border}`}}>
            Ingresá FOB y CBM para ver el cálculo
          </div>
        )}
      </div>
    </div>
  );
}
